import { useMemo, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { ExternalLink, GitBranch, Globe, Lock, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, Tabs, TabsContent, TabsList, TabsTrigger } from "@fox1ck-org/ui";
import {
  useCreateGatewayRoute,
  useDeleteGatewayRoute,
  useGatewayRoutePreview,
  useGatewayRoutes,
} from "@/hooks/use-gateway-routes";
import type { GatewayRouteSpec, GatewayRouteSummary } from "@/api/gateway";

const EMPTY_SPEC: GatewayRouteSpec = {
  app: "",
  component: "",
  namespace: "",
  host: "",
  pathPrefix: "/",
  backend: { service: "", port: 3000 },
  auth: { required: true, optional: false, requiredRealmRoles: [] },
};

function AuthBadge({ spec }: { spec: GatewayRouteSpec }) {
  if (!spec.auth.required) {
    return (
      <Badge variant="outline" className="gap-1">
        <Globe className="h-3 w-3" /> Публичный
      </Badge>
    );
  }
  if (spec.auth.optional) {
    return (
      <Badge variant="neutral" className="gap-1">
        <Lock className="h-3 w-3" /> JWT необязателен
      </Badge>
    );
  }
  return (
    <Badge variant="primary" className="gap-1">
      <Lock className="h-3 w-3" /> JWT обязателен
    </Badge>
  );
}

const columns: ColumnDef<GatewayRouteSummary>[] = [
  {
    accessorKey: "spec.host",
    header: "Хост",
    cell: ({ row }) => (
      <span className="font-mono text-sm font-medium">{row.original.spec.host}</span>
    ),
  },
  {
    accessorKey: "spec.app",
    header: "Приложение",
    cell: ({ row }) => (
      <Badge variant="outline">
        {row.original.spec.app}
        {row.original.spec.component ? ` / ${row.original.spec.component}` : ""}
      </Badge>
    ),
  },
  {
    accessorKey: "spec.backend",
    header: "Бэкенд",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground font-mono">
        {row.original.spec.backend.service}:{row.original.spec.backend.port}
      </span>
    ),
  },
  {
    accessorKey: "spec.auth",
    header: "Авторизация",
    cell: ({ row }) => <AuthBadge spec={row.original.spec} />,
  },
  {
    accessorKey: "spec.rateLimit",
    header: "Лимит запросов",
    cell: ({ row }) =>
      row.original.spec.rateLimit ? (
        <span className="text-sm">
          {row.original.spec.rateLimit.requests}/{row.original.spec.rateLimit.unit.toLowerCase()}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: "openPrUrl",
    header: "Открытый PR",
    cell: ({ row }) =>
      row.original.openPrUrl ? (
        <a
          href={row.original.openPrUrl}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-1 text-sm"
        >
          <GitBranch className="h-3 w-3" /> open <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      ),
  },
];

export default function GatewayRoutes() {
  const { data, isLoading, refetch } = useGatewayRoutes();
  const create = useCreateGatewayRoute();
  const remove = useDeleteGatewayRoute();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState<GatewayRouteSpec>(EMPTY_SPEC);
  const [rolesInput, setRolesInput] = useState("");
  const [rateLimitOn, setRateLimitOn] = useState(false);
  const [rateRequests, setRateRequests] = useState(200);
  const [rateUnit, setRateUnit] = useState<GatewayRouteSpec["rateLimit"] extends infer T ? T extends { unit: infer U } ? U : never : never>("Second");

  const rows = useMemo(() => data?.routes ?? [], [data]);

  const previewSpec: GatewayRouteSpec | null = useMemo(() => {
    if (!sheetOpen) return null;
    const trimmed: GatewayRouteSpec = {
      ...form,
      component: form.component?.trim() || undefined,
      namespace: form.namespace?.trim() || undefined,
      auth: {
        ...form.auth,
        requiredRealmRoles: rolesInput
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      },
      rateLimit: rateLimitOn ? { requests: rateRequests, unit: rateUnit } : undefined,
    };
    return trimmed.host && trimmed.app && trimmed.backend.service ? trimmed : null;
  }, [sheetOpen, form, rolesInput, rateLimitOn, rateRequests, rateUnit]);

  const preview = useGatewayRoutePreview(previewSpec);

  const submitDisabled = !previewSpec || create.isPending;

  async function onSubmit() {
    if (!previewSpec) return;
    try {
      const res = await create.mutateAsync(previewSpec);
      toast.success(
        `PR #${res.pr.number} открыт`,
        {
          description: previewSpec.host,
          action: {
            label: "Открыть",
            onClick: () => window.open(res.pr.url, "_blank"),
          },
        },
      );
      setSheetOpen(false);
      setForm(EMPTY_SPEC);
      setRolesInput("");
      setRateLimitOn(false);
    } catch (err) {
      toast.error("Не удалось открыть PR", {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async function onDelete(spec: GatewayRouteSpec) {
    if (!window.confirm(`Открыть PR на удаление маршрута ${spec.host}?`)) return;
    try {
      const res = await remove.mutateAsync(spec);
      toast.success(`PR #${res.pr.number} на удаление открыт`, {
        action: { label: "Открыть", onClick: () => window.open(res.pr.url, "_blank") },
      });
    } catch (err) {
      toast.error("Не удалось открыть PR на удаление", {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Маршруты шлюза"
        description="Маршруты приложений, JWT и лимиты запросов в Envoy Gateway. Каждая правка открывает PR в репозиторий инфраструктуры."
      >
        <Button variant="ghost" size="icon" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
        <Button onClick={() => setSheetOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Новый маршрут
        </Button>
      </PageHeader>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        emptyState={
          <EmptyState
            icon={<Globe className="h-10 w-10" />}
            title="Управляемых маршрутов пока нет"
            description="Нажмите «Новый маршрут», чтобы открыть PR с первым. Маршруты, написанные вручную в clusters/base/<app>/, появятся после создания индекса."
          />
        }
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Новый маршрут шлюза</SheetTitle>
            <SheetDescription>
              Откроется PR с HTTPRoute, SecurityPolicy и BackendTrafficPolicy в
              clusters/base/&lt;app&gt;/. Применится после мержа.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Папка приложения</label>
                <Input
                  placeholder="fb"
                  value={form.app}
                  onChange={(e) => setForm({ ...form, app: e.target.value.trim() })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Подкомпонент (необязательно)</label>
                <Input
                  placeholder="app-manager"
                  value={form.component ?? ""}
                  onChange={(e) => setForm({ ...form, component: e.target.value.trim() })}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Хост</label>
              <Input
                placeholder="fb.igrudsky.dev"
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value.trim() })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Префикс пути</label>
                <Input
                  placeholder="/"
                  value={form.pathPrefix}
                  onChange={(e) => setForm({ ...form, pathPrefix: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Пространство имён (по умолчанию приложение)</label>
                <Input
                  placeholder=""
                  value={form.namespace ?? ""}
                  onChange={(e) => setForm({ ...form, namespace: e.target.value.trim() })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Сервис бэкенда</label>
                <Input
                  placeholder="fb-api"
                  value={form.backend.service}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      backend: { ...form.backend, service: e.target.value.trim() },
                    })
                  }
                />
              </div>
              <div>
                <label className="text-sm font-medium">Порт бэкенда</label>
                <Input
                  type="number"
                  value={form.backend.port}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      backend: { ...form.backend, port: Number(e.target.value) || 0 },
                    })
                  }
                />
              </div>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Авторизация</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select
                  value={
                    !form.auth.required
                      ? "public"
                      : form.auth.optional
                        ? "optional"
                        : "required"
                  }
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      auth: {
                        ...form.auth,
                        required: v !== "public",
                        optional: v === "optional",
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="required">JWT обязателен (анонимам отказ)</SelectItem>
                    <SelectItem value="optional">
                      JWT необязателен (принимается и X-API-Key)
                    </SelectItem>
                    <SelectItem value="public">Публичный (без авторизации на шлюзе)</SelectItem>
                  </SelectContent>
                </Select>

                {form.auth.required ? (
                  <div>
                    <label className="text-sm font-medium">
                      Обязательные роли реалма (через запятую, пусто = любой пользователь)
                    </label>
                    <Input
                      placeholder="fb-admin, fb-member"
                      value={rolesInput}
                      onChange={(e) => setRolesInput(e.target.value)}
                    />
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Лимит запросов (локальный, на пользователя)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select
                  value={rateLimitOn ? "on" : "off"}
                  onValueChange={(v) => setRateLimitOn(v === "on")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Без лимита на шлюзе</SelectItem>
                    <SelectItem value="on">Лимит на X-Identity-Sub</SelectItem>
                  </SelectContent>
                </Select>

                {rateLimitOn ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">Запросы</label>
                      <Input
                        type="number"
                        value={rateRequests}
                        onChange={(e) => setRateRequests(Number(e.target.value) || 1)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">За</label>
                      <Select
                        value={rateUnit}
                        onValueChange={(v) =>
                          setRateUnit(v as typeof rateUnit)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Second">Секунда</SelectItem>
                          <SelectItem value="Minute">Минута</SelectItem>
                          <SelectItem value="Hour">Час</SelectItem>
                          <SelectItem value="Day">День</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Предпросмотр</CardTitle>
              </CardHeader>
              <CardContent>
                {preview.data ? (
                  <Tabs defaultValue="httproute">
                    <TabsList>
                      <TabsTrigger value="httproute">HTTPRoute</TabsTrigger>
                      <TabsTrigger value="security" disabled={!preview.data.yamls.securityPolicy}>
                        SecurityPolicy
                      </TabsTrigger>
                      <TabsTrigger
                        value="traffic"
                        disabled={!preview.data.yamls.backendTrafficPolicy}
                      >
                        TrafficPolicy
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="httproute">
                      <pre className="text-xs bg-muted/40 p-3 rounded font-mono overflow-x-auto">
                        {preview.data.yamls.httpRoute}
                      </pre>
                    </TabsContent>
                    <TabsContent value="security">
                      <pre className="text-xs bg-muted/40 p-3 rounded font-mono overflow-x-auto">
                        {preview.data.yamls.securityPolicy ?? ""}
                      </pre>
                    </TabsContent>
                    <TabsContent value="traffic">
                      <pre className="text-xs bg-muted/40 p-3 rounded font-mono overflow-x-auto">
                        {preview.data.yamls.backendTrafficPolicy ?? ""}
                      </pre>
                    </TabsContent>
                  </Tabs>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Заполните приложение, хост и сервис бэкенда, чтобы увидеть манифесты.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <SheetFooter className="gap-2">
            <Button variant="outline" onClick={() => setSheetOpen(false)}>
              Отмена
            </Button>
            <Button onClick={onSubmit} disabled={submitDisabled}>
              {create.isPending ? "Открываем PR…" : "Открытый PR"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {rows.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Быстрые действия</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {rows.map((r) => (
              <div
                key={`${r.spec.app}-${r.spec.component ?? "root"}`}
                className="flex items-center justify-between text-sm"
              >
                <span className="font-mono">{r.spec.host}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(r.spec)}
                  className="text-destructive"
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Удалить
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
